import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { countries } from "@/constants/countryCodes";
import { registrationTypes } from "@/constants/registrationTypes";
import { states } from "@/constants/states";
import { useToast } from "@/hooks/use-toast";
import { useSelfService } from "@/hooks/useSelfService";
import {
  selfServiceBuyerDetails,
  SelfServiceBuyerForm,
  SelfServiceInvoiceDetails,
} from "@/schema/self-service.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Badge, Box, Tabs } from "@radix-ui/themes";
import {
  Building2,
  Loader2,
  Mail,
  Phone,
  SearchX,
  Settings,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "wouter";
import { navigate } from "wouter/use-browser-location";
import logo from "/asset/logo-removebg-preview.png";
import TinSearch from "@/components/modal-tin";

const SelfServiceDocumentPage = () => {
  const invoiceId = useParams();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [openTinSearch, setOpenTinSearch] = useState<boolean>(false);
  const [isReadonly, setIsReadonly] = useState<boolean>(false);
  const [statusCheckCode, setStatusCheckCode] = useState<string | null>(null);
  const statusCodeRef = useRef<HTMLSpanElement>(null);

  const {
    invoice,
    isLoading,
    isLoadingError,
    editBuyerDetailsAsync,
    isSubmittingBuyerDetails,
  } = useSelfService(invoiceId.id);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<SelfServiceInvoiceDetails>({
    resolver: zodResolver(selfServiceBuyerDetails),
    defaultValues: invoice,
  });

  useEffect(() => {
    reset(invoice);

    // Form is readonly if:
    // 1. Invoice is consolidated, OR
    // 2. Status is "Valid" or "Submitted"
    setIsReadonly(
      (invoice?.isConsolidated ?? false) ||
        invoice?.status === "Valid" ||
        invoice?.status === "Submitted",
    );
  }, [invoice]);

  const [lineItems] = watch(["lineItems"]);

  const onSubmit = async (data: SelfServiceBuyerForm) => {
    const form = {
      id: invoiceId.id as string,
      formData: data,
    };
    await editBuyerDetailsAsync(form, {
      onSuccess: (response: any) => {
        const status = response?.data?.status;
        const code: string | undefined = response?.data?.statusCheckCode;

        if (response?.data?.message?.includes("LHDN submission failed")) {
          const lhdnError = response?.data?.lhdnError;
          const lhdnReason =
            lhdnError?.error?.message ??
            lhdnError?.message ??
            (typeof lhdnError === "string" ? lhdnError : null);
          toast({
            title: "Submission Issue",
            description: lhdnReason
              ? `Your details were saved but LHDN rejected the submission: ${lhdnReason}`
              : "Your details were saved, but there was an issue submitting to LHDN. Please try again later.",
            variant: "destructive",
            duration: 12000,
          });
          return;
        }

        if (code) {
          setStatusCheckCode(code);
        }

        if (status === "Valid") {
          toast({
            title: "Invoice Submitted to LHDN",
            description: `Your invoice was validated successfully. To check submission status, visit /self-service/check and use code: ${code ?? "—"}.`,
            duration: 10000,
          });
          setSubmitted(true);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else if (status === "Invalid") {
          toast({
            title: "Invoice Submitted to LHDN",
            description: `Your invoice was rejected by LHDN. Use code ${code ?? "—"} at /self-service/check to view error details and resubmit.`,
            variant: "destructive",
            duration: 10000,
          });
          setSubmitted(false);
        } else {
          toast({
            title: "Invoice Submitted to LHDN",
            description: `Your invoice is being processed. Use code ${code ?? "—"} at /self-service/check to track the status.`,
            duration: 10000,
          });
          setSubmitted(true);
        }
      },
      onError: (error: any) => {
        toast({
          title: "Failed",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to submit. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      },
    });
  };

  const onError = () => {
    toast({
      title: "Action failed",
      description: "Please fill in all required fields",
      duration: 4000,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft":
        return "gray";
      case "Submitted":
        return "blue";
      case "Valid":
        return "green";
      case "Invalid":
        return "red";
      case "Consolidated":
        return "brown";
      default:
        return "gray";
    }
  };

  if (isLoadingError) {
    return <ErrorBoundary />;
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-white pt-[80px] pb-8 px-4">
      <SelfServiceNav />
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <Card className="w-full max-w-6xl mx-auto shadow pt-6">
          <CardContent>
            <form
              className="space-y-6"
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-6">
                <div className="flex flex-row">
                  <h1 className="text-xl font-bold text-center md:text-left me-2">
                    Invoice
                  </h1>
                  <Badge
                    color={getStatusColor(
                      invoice?.isConsolidated
                        ? "Consolidated"
                        : invoice?.status || "Draft",
                    )}
                    size="3"
                    className="border border-3"
                  >
                    {invoice?.isConsolidated
                      ? "Consolidated"
                      : invoice?.status || "Draft"}
                  </Badge>
                </div>
                {/* Company Details - Middle */}
                <div className="flex flex-col items-center justify-center w-full text-center space-y-3 md:-ml-16">
                  <div className="flex flex-row items-center justify-center gap-4">
                    <h1 className="text-2xl font-bold text-center md:text-left">
                      {getValues("supplierName")}
                    </h1>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone size={16} className="flex-shrink-0" />
                    <span className="text-sm">
                      {getValues("supplierContact")}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail size={16} className="flex-shrink-0" />
                    <span className="text-sm">
                      {getValues("supplierEmail")}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Building2 size={16} className="flex-shrink-0" />
                    <span className="text-sm">
                      {getValues("supplierAddressLine0")},{" "}
                      {getValues("supplierCity")}
                    </span>
                  </div>
                </div>

                <Separator orientation="vertical" />
              </div>

              <Tabs.Root defaultValue="buyer">
                <Tabs.List color="gray" justify="start">
                  <Tabs.Trigger value="buyer">
                    Buyer Information
                    <span className="text-red-600">*</span>
                  </Tabs.Trigger>
                </Tabs.List>

                <Box pt="3">
                  <Tabs.Content value="buyer">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Buyer Information{" "}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="buyerRegistrationType" required>
                            ID Type
                          </Label>
                          <Controller
                            name="buyerRegistrationType"
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={submitted || isReadonly}
                              >
                                <SelectTrigger
                                  className="h-10"
                                  errors={!!errors.buyerRegistrationType}
                                >
                                  <SelectValue placeholder="-- Select type --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {registrationTypes.map((type) => (
                                    <SelectItem
                                      key={type.value}
                                      value={type.value}
                                    >
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.buyerRegistrationType && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerRegistrationType.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerRegistrationNumber" required>
                            Registration / Identification / Passport Number
                          </Label>
                          <Input
                            {...register("buyerRegistrationNumber")}
                            errors={!!errors.buyerRegistrationNumber}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerRegistrationNumber && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerRegistrationNumber.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerTin" required>
                            TIN
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              {...register("buyerTin")}
                              errors={!!errors.buyerTin}
                              disabled={submitted || isReadonly}
                            />
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenTinSearch(true)}
                              className="group"
                              disabled={
                                isSubmittingBuyerDetails ||
                                submitted ||
                                isReadonly
                              }
                            >
                              <Settings />
                            </Button>
                          </div>
                          {errors.buyerTin && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerTin.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="buyerName" required>
                            Name
                          </Label>
                          <Input
                            {...register("buyerName")}
                            errors={!!errors.buyerName}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerName && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerName.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerSstRegistrationNumber">
                            SST Registration Number
                          </Label>
                          <Input
                            {...register("buyerSstRegistrationNumber")}
                            errors={!!errors.buyerSstRegistrationNumber}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerSstRegistrationNumber && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerSstRegistrationNumber.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerEmail" required>
                            Email
                          </Label>
                          <Input
                            {...register("buyerEmail")}
                            errors={!!errors.buyerEmail}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerEmail && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerEmail.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerContact" required>
                            Contact Number
                          </Label>
                          <Input
                            {...register("buyerContact")}
                            errors={!!errors.buyerContact}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerContact && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerContact.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="buyerAddressLine0" required>
                          Address Line 1
                        </Label>
                        <Input
                          {...register("buyerAddressLine0")}
                          errors={!!errors.buyerAddressLine0}
                          disabled={submitted || isReadonly}
                        />
                        {errors.buyerAddressLine0 && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerAddressLine0.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerAddressLine1">
                          Address Line 2
                        </Label>
                        <Input
                          {...register("buyerAddressLine1")}
                          errors={!!errors.buyerAddressLine1}
                          disabled={submitted || isReadonly}
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyerAddressLine2">
                          Address Line 3
                        </Label>
                        <Input
                          {...register("buyerAddressLine2")}
                          errors={!!errors.buyerAddressLine2}
                          disabled={submitted || isReadonly}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="buyerCity" required>
                            City
                          </Label>
                          <Input
                            {...register("buyerCity")}
                            errors={!!errors.buyerCity}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerCity && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerCity.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerPostcode">Postcode</Label>
                          <Input
                            {...register("buyerPostcode")}
                            errors={!!errors.buyerPostcode}
                            disabled={submitted || isReadonly}
                          />
                          {errors.buyerPostcode && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerPostcode.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerCountry" required>
                            Country
                          </Label>
                          <Controller
                            name="buyerCountry"
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={submitted || isReadonly}
                              >
                                <SelectTrigger
                                  className="h-10"
                                  errors={!!errors.buyerCountry}
                                >
                                  <SelectValue placeholder="-- Select Country --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map((country) => (
                                    <SelectItem
                                      key={country.Code}
                                      value={country.Code}
                                    >
                                      {country.Country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.buyerCountry && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerCountry.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="buyerState" required>
                            State
                          </Label>
                          <Controller
                            name="buyerState"
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={submitted || isReadonly}
                              >
                                <SelectTrigger
                                  className="h-10"
                                  errors={!!errors.buyerState}
                                >
                                  <SelectValue placeholder="-- Select State --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {states.map((state) => (
                                    <SelectItem
                                      key={state.Code}
                                      value={state.Code}
                                    >
                                      {state.State}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.buyerState && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.buyerState.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Tabs.Content>
                </Box>
              </Tabs.Root>

              {/* Items Section */}
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-36">Classification</TableHead>
                      <TableHead className="min-w-36">Description</TableHead>
                      <TableHead className="min-w-36">Unit Price</TableHead>
                      <TableHead className="min-w-36">Tax Type</TableHead>
                      <TableHead className="min-w-36">Tax Rate</TableHead>
                      <TableHead className="min-w-36">Tax Amount</TableHead>
                      <TableHead className="min-w-36">
                        Details of Tax Exemption
                      </TableHead>
                      <TableHead className="min-w-36">
                        Amount Exempted from Tax
                      </TableHead>
                      <TableHead className="min-w-36">Quantity</TableHead>
                      <TableHead className="min-w-36">Measurement</TableHead>
                      <TableHead className="min-w-36">Discount Rate</TableHead>
                      <TableHead className="min-w-36">
                        Fee / Charge Rate
                      </TableHead>
                      <TableHead className="min-w-36">
                        Product Tariff Code
                      </TableHead>
                      <TableHead className="min-w-36">
                        Country of Origin
                      </TableHead>
                      <TableHead className="min-w-36">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems ? (
                      lineItems.map((item, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Input value={item.classification} disabled />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="w-72"
                                value={item.description}
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.unitPrice}
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input value={item.taxType} disabled />
                            </TableCell>
                            <TableCell>
                              <Input value={item.taxRate} disabled />
                            </TableCell>
                            <TableCell>
                              <Input value={item.taxAmount} disabled />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.detailsOfTaxExemption ?? ""}
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.amountExemptedFromTax ?? ""}
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.quantity}
                                className="w-20"
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.measurement ?? ""}
                                className="w-48"
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.discountRate}
                                className="w-20"
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.feeChargeRate}
                                className="w-20"
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.productTariffCode ?? ""}
                                className="w-20"
                                disabled
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.countryOfOrigin ?? ""}
                                disabled
                              />
                            </TableCell>
                            <TableCell className="">
                              <Input
                                type="number"
                                value={
                                  Number(item.subtotal) + Number(item.taxAmount)
                                }
                                disabled
                                className="border-0"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={15}>Loading..</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Totals */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total Excluding Tax:</span>
                    <span>{`RM ${getValues("totalExcludingTax")}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Including Tax:</span>
                    <span>{`RM ${getValues("totalIncludingTax")}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Tax Amount:</span>
                    <span>{`RM ${getValues("totalTaxAmount")}`} </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold">
                    <span>Total Payable Amount:</span>
                    <span>{`RM ${getValues("totalPayableAmount")}`}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-3 pt-2">
                {statusCheckCode && (
                  <div className="flex flex-wrap items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                    <span className="text-blue-700">Submitted</span>
                    <span className="text-blue-400">·</span>
                    <span className="text-blue-600">
                      Check Status Code:{" "}
                      <span
                        ref={statusCodeRef}
                        className="font-mono font-bold tracking-widest text-blue-800 select-all"
                      >
                        {statusCheckCode}
                      </span>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() =>
                        navigate(`/self-service/check?code=${statusCheckCode}`)
                      }
                    >
                      Check Status
                    </Button>
                  </div>
                )}
                <div className="flex justify-end w-full">
                  {submitted || isReadonly ? (
                    <Button
                      type="button"
                      onClick={() => navigate("/self-service")}
                    >
                      Close
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="w-full md:w-auto"
                      disabled={isSubmittingBuyerDetails}
                    >
                      {isSubmittingBuyerDetails ? (
                        <>
                          <Loader2 className="animate-spin text-primary" />
                          Submitting to LHDN...
                        </>
                      ) : invoice?.status === "Invalid" ? (
                        "Fix & Resubmit to LHDN"
                      ) : (
                        "Submit to LHDN"
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardContent>
          <TinSearch
            isOpen={openTinSearch}
            onClose={() => setOpenTinSearch(false)}
            onSelect={(value) => setValue("buyerTin", value)}
          />
        </Card>
      )}
    </div>
  );
};

const SelfServiceNav = () => {
  return (
    <nav className="w-full fixed z-30 top-0 p-2 bg-white shadow-md border-b border-slate-200 flex justify-center">
      <img
        className="h-16"
        // src="/logo-removebg-preview.png"
        src={logo}
      />
    </nav>
  );
};

const LoadingIndicator = () => {
  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">
          Loading...
        </p>
      </div>
    </div>
  );
};

const ErrorBoundary = () => {
  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-white pt-[80px] pb-8 px-4">
      <SelfServiceNav />
      <Card className="w-full max-w-4xl mx-auto shadow pt-6">
        <CardContent>
          <div className="flex flex-col items-center justify-center text-gray-400 p-4 gap-4">
            <SearchX size={200} />
            <p>No invoice found.</p>
            <Button size="sm" onClick={() => navigate("/self-service")}>
              Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SelfServiceDocumentPage;
